import type { UiWalletAccount } from "@mysten/dapp-kit-react";
import { useCurrentAccount, useDAppKit, useWalletConnection } from "@mysten/dapp-kit-react";
import { useMutation, UseMutationResult } from "@tanstack/react-query";

/**
 * dapp-kit v2 removed the mutation hooks that v1 exposed
 * (`useSignPersonalMessage`, `useSignAndExecuteTransaction`,
 * `useDisconnectWallet`, `useSwitchAccount`, `useAccounts`) — they became plain
 * async actions on the dApp kit instance.
 *
 * These wrappers re-expose them with the v1 react-query surface
 * (`mutate`/`mutateAsync` + `onSuccess`/`onError`), so call sites keep working
 * without each one hand-rolling a `useMutation`. Prefer the instance actions
 * directly (`useDAppKit()`) in new code.
 */

// v2 does not export its action argument types, so derive them from the
// registered instance — that also keeps the network union in sync.
type DAppKitInstance = ReturnType<typeof useDAppKit>;

type SignPersonalMessageArgs = Parameters<DAppKitInstance["signPersonalMessage"]>[0];

type SignPersonalMessageResult = Awaited<ReturnType<DAppKitInstance["signPersonalMessage"]>>;

type SignAndExecuteTransactionArgs = Parameters<DAppKitInstance["signAndExecuteTransaction"]>[0];

type SignAndExecuteTransactionResult = Awaited<
  ReturnType<DAppKitInstance["signAndExecuteTransaction"]>
>;

/** v1 `useAccounts()` — all authorized accounts of the connected wallet. */
export function useAccounts(): readonly UiWalletAccount[] {
  const { wallet } = useWalletConnection();

  return wallet?.accounts ?? [];
}

/** v1 `useDisconnectWallet()`. */
export function useDisconnectWallet(): UseMutationResult<void, Error, void, unknown> {
  const dAppKit = useDAppKit();

  return useMutation({
    mutationKey: ["dappkit", "disconnectWallet"],
    mutationFn: () => dAppKit.disconnectWallet(),
  });
}

/**
 * v1 `useSwitchAccount()`. Note `switchAccount` is synchronous in v2 and throws
 * on failure; it is wrapped so existing `mutate(..., { onSuccess })` call sites
 * behave the same.
 */
export function useSwitchAccount(): UseMutationResult<
  void,
  Error,
  { account: UiWalletAccount },
  unknown
> {
  const dAppKit = useDAppKit();

  return useMutation({
    mutationKey: ["dappkit", "switchAccount"],
    mutationFn: async ({ account }: { account: UiWalletAccount }) =>
      dAppKit.switchAccount({ account }),
  });
}

/** v1 `useSignPersonalMessage()`. Result is still `{ bytes, signature }`. */
export function useSignPersonalMessage(): UseMutationResult<
  SignPersonalMessageResult,
  Error,
  SignPersonalMessageArgs,
  unknown
> {
  const dAppKit = useDAppKit();

  return useMutation({
    mutationKey: ["dappkit", "signPersonalMessage"],
    mutationFn: (args: SignPersonalMessageArgs) => dAppKit.signPersonalMessage(args),
  });
}

/**
 * v1 `useSignAndExecuteTransaction()`.
 *
 * IMPORTANT behaviour change in v2: a transaction that executes but aborts
 * on-chain RESOLVES with `$kind: 'FailedTransaction'` instead of rejecting. Call
 * sites here were written against v1, where a failure surfaced through
 * `onError`, so this wrapper rethrows failed transactions to preserve that
 * contract and unwraps the union to the `{ digest, effects, ... }` object the
 * existing success callbacks read.
 */
export function useSignAndExecuteTransaction(): UseMutationResult<
  SignAndExecuteTransactionResult["Transaction"],
  Error,
  SignAndExecuteTransactionArgs,
  unknown
> {
  const dAppKit = useDAppKit();

  return useMutation({
    mutationKey: ["dappkit", "signAndExecuteTransaction"],
    mutationFn: async (args: SignAndExecuteTransactionArgs) => {
      const result = await dAppKit.signAndExecuteTransaction(args);

      if (result.$kind === "FailedTransaction") {
        throw new Error(
          result.FailedTransaction.status.error?.message ??
            `Transaction ${result.FailedTransaction.digest} failed`,
        );
      }

      return result.Transaction;
    },
  });
}

export { useCurrentAccount };
